import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { Task } from '@lit/task'
import { sanitizeRichTextHtml } from '../../lib/sanitize.js'

/**
 * Help article component for displaying contextual help content
 *
 * @fires article-loaded - Fired when article content is loaded
 * @fires article-error - Fired when article fails to load
 *
 * @slot - Default slot for article content
 * @slot actions - Action buttons for the article
 *
 * @csspart article - The article container
 * @csspart header - The article header
 * @csspart content - The article content area
 * @csspart footer - The article footer
 */
@customElement('cairn-help-article')
export class CairnHelpArticle extends LitElement {
  static override styles = css`
    :host {
      display: block;
      font-family: var(
        --cairn-font-family,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        Roboto,
        sans-serif
      );
      line-height: var(--cairn-line-height, 1.5);
      color: var(--cairn-text-color, #1e293b);
      /* Add bottom padding for mobile navigation */
      padding-bottom: calc(var(--cairn-spacing-lg, 24px) + 60px);
    }

    .article {
      background: var(--cairn-surface-color, #ffffff);
      border-radius: var(--cairn-border-radius, 8px);
      box-shadow: var(--cairn-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));
      overflow: hidden;
    }

    .header {
      padding: var(--cairn-spacing-lg, 24px);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      background: var(--cairn-surface-variant-color, #f8fafc);
    }

    .title {
      margin: 0;
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-primary-color, #0f172a);
    }

    .subtitle {
      margin: var(--cairn-spacing-sm, 8px) 0 0 0;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .content {
      padding: var(--cairn-spacing-lg, 24px);
    }

    .content ::slotted(*) {
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .content ::slotted(:last-child) {
      margin-bottom: 0;
    }

    .footer {
      padding: var(--cairn-spacing-lg, 24px);
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
      background: var(--cairn-surface-variant-color, #f8fafc);
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 200px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .error {
      padding: var(--cairn-spacing-lg, 24px);
      color: var(--cairn-error-color, #dc2626);
      text-align: center;
    }

    .offline-indicator {
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
      background: var(--cairn-warning-color, #f59e0b);
      color: var(--cairn-warning-text-color, #ffffff);
      font-size: var(--cairn-text-size-sm, 14px);
      text-align: center;
    }

    /* Responsive design */
    @media (max-width: 768px) {
      .header,
      .content,
      .footer {
        padding: var(--cairn-spacing-md, 16px);
      }

      .title {
        font-size: var(--cairn-text-size-lg, 20px);
      }
    }

    /* Dark mode support */
    @media (prefers-color-scheme: dark) {
      :host {
        color: var(--cairn-text-color-dark, #f1f5f9);
      }

      .article {
        background: var(--cairn-surface-color-dark, #1e293b);
        box-shadow: var(--cairn-shadow-dark, 0 2px 4px rgba(0, 0, 0, 0.3));
      }

      .header,
      .footer {
        background: var(--cairn-surface-variant-color-dark, #334155);
        border-color: var(--cairn-border-color-dark, #475569);
      }

      .title {
        color: var(--cairn-text-primary-color-dark, #f8fafc);
      }
    }

    /* High contrast mode */
    @media (prefers-contrast: high) {
      .article {
        border: 2px solid var(--cairn-text-color, #1e293b);
      }

      .header,
      .footer {
        border-width: 2px;
      }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      * {
        transition: none !important;
        animation: none !important;
      }
    }
  `

  @property() articleId: string = ''
  @property() override title: string = ''
  @property() subtitle: string = ''
  @property({ type: Boolean }) offlineMode: boolean = false
  @property({ type: Boolean }) loading: boolean = false
  @property() errorMessage: string = ''

  private _loadArticleTask = new Task(this, {
    task: async ([articleId]): Promise<string> => {
      if (!articleId) {
        return ''
      }

      try {
        // Simulate loading article content
        // In real implementation, this would fetch from local storage or API
        const response = await fetch(`/assets/help/${articleId}.md`)
        if (!response.ok) {
          throw new Error(`Failed to load article: ${response.statusText}`)
        }
        return await response.text()
      } catch (error) {
        this.dispatchEvent(
          new CustomEvent('article-error', {
            detail: { error, articleId },
            bubbles: true,
          })
        )
        throw error
      }
    },
    args: (): [string] => [this.articleId],
  })

  override connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener('keydown', this._handleKeydown)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListener('keydown', this._handleKeydown)
  }

  private _handleKeydown(event: KeyboardEvent): void {
    // Handle keyboard navigation for accessibility
    if (event.key === 'Escape') {
      this.dispatchEvent(
        new CustomEvent('article-close', {
          bubbles: true,
        })
      )
    }
  }

  protected override render(): unknown {
    return html`
      <article class="article" part="article">
        ${this.offlineMode
          ? html`
              <div class="offline-indicator">
                📡 Viewing cached content - some features may be unavailable
              </div>
            `
          : ''}
        ${this.title || this.subtitle
          ? html`
              <header class="header" part="header">
                ${this.title ? html`<h1 class="title">${this.title}</h1>` : ''}
                ${this.subtitle ? html`<p class="subtitle">${this.subtitle}</p>` : ''}
              </header>
            `
          : ''}

        <div class="content" part="content">
          ${this._loadArticleTask.render({
            pending: (): unknown => html`
              <div class="loading">
                <div>Loading article...</div>
              </div>
            `,
            complete: (content: string): unknown => {
              this.dispatchEvent(
                new CustomEvent('article-loaded', {
                  detail: { content, articleId: this.articleId },
                  bubbles: true,
                })
              )
              // In a real implementation, you'd parse and render markdown here
              return html`
                <div .innerHTML="${sanitizeRichTextHtml(content)}"></div>
                <slot></slot>
              `
            },
            error: (error: unknown): unknown => html`
              <div class="error">
                <p>Failed to load article content.</p>
                <p><small>${error instanceof Error ? error.message : String(error)}</small></p>
              </div>
            `,
          })}
        </div>

        <footer class="footer" part="footer">
          <slot name="actions"></slot>
        </footer>
      </article>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-help-article': CairnHelpArticle
  }
}
